import type { ComponentProps } from 'react'

import { type Icon } from '@factorialco/deprecated-design-system'
import { NotificationsNotifierScopesQuery } from 'generated/resources/graphql'

import createComputed from 'modules/core/hooks/createComputed'

import type Notifier from 'modules/notifications/store/models/Notifier'
import type UserPreference from 'modules/notifications/store/models/UserPreference'

type NotificationsAccessNotifierScope = NonNullable<
  NotificationsNotifierScopesQuery['notifications']['accessNotifierScopes']
>[number]

export interface NotifierChannelData {
  editable: boolean
}

export interface NotifierPreferences {
  identifier: string
  title: string
  description: string
  channelData: Record<string, NotifierChannelData>
  channelPreferences: Record<string, UserPreference>
  // Scope identifier to translation
  availableScopes?: Record<string, string>
  currentScope?: string
  orderInCategory?: number
}

export interface CategoryPreferences {
  identifier: string
  title: string
  icon: ComponentProps<typeof Icon.Large>['icon']
  notifiers: Record<string, NotifierPreferences>
}

export interface ComputedPreferences {
  categories: Record<string, CategoryPreferences>
}

export const useComputedPreferences = createComputed(
  (
    notifiers: Notifier[],
    preferences: UserPreference[],
    notifierScopes: NotificationsAccessNotifierScope[],
    filter: string
  ) => {
    const computedPreferences: ComputedPreferences = {
      categories: {},
    }

    const normalizedFilter = filter.toLowerCase()

    const filteredNotifiers =
      filter === ''
        ? notifiers
        : notifiers.filter(
            (notifier) =>
              notifier.title.toLowerCase().includes(normalizedFilter) ||
              notifier.description.toLowerCase().includes(normalizedFilter)
          )

    const filteredNotifiersByIdentifier = filteredNotifiers.reduce<Record<string, Notifier>>(
      (acc, notifier) => {
        acc[notifier.identifier] = notifier

        return acc
      },
      {}
    )

    const currentScopeByNotifier = notifierScopes.reduce<Record<string, string>>((acc, scope) => {
      acc[scope.notifier] = scope.value

      return acc
    }, {})

    // Compute preferences
    preferences.forEach((preference) => {
      const notifier = filteredNotifiersByIdentifier[preference.notifier]

      if (!notifier) {
        return
      }

      if (!computedPreferences.categories[notifier.category]) {
        computedPreferences.categories[notifier.category] = {
          identifier: notifier.category,
          title: notifier.categoryTitle,
          icon: notifier.categoryIcon,
          notifiers: {},
        }
      }

      const categoryPreferences = computedPreferences.categories[notifier.category]!

      if (!categoryPreferences.notifiers[notifier.identifier]) {
        categoryPreferences.notifiers[notifier.identifier] = {
          identifier: notifier.identifier,
          title: notifier.title,
          description: notifier.description,
          channelData: {},
          channelPreferences: {},
          orderInCategory: notifier.orderInCategory,
          availableScopes: notifier.scopes,
          currentScope: currentScopeByNotifier[notifier.identifier],
        }
      }

      const notifierPreferences = categoryPreferences.notifiers[notifier.identifier]!

      notifierPreferences.channelPreferences[preference.channel] = preference
    })

    // Compute channel data
    notifiers.forEach((notifier) => {
      Object.entries(notifier.channels).forEach(([channel, channelData]) => {
        const categoryPreferences = computedPreferences.categories[notifier.category]
        const notifierPreferences = categoryPreferences?.notifiers[notifier.identifier]

        if (!notifierPreferences) {
          return
        }

        notifierPreferences.channelData[channel] = {
          editable: channelData.editable,
        }
      })
    })

    return computedPreferences
  }
)
